import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { colors, fonts, radii, breakpoints, MonitorIcon, PhotoCameraIcon } from '../../../ui';

// Building blocks shared by the clinician (C2-C5) and patient (P3-P6) calibration screens.

const phone = `@media (max-width: ${breakpoints.phone - 1}px)`;

// Vertically centred panel content (the Figma panels centre their content).
export const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ gap = 16 }) => gap}px;
  width: 100%;
  margin: ${({ top }) => (top ? '0' : 'auto 0')};
  font-family: ${fonts.family};
  text-align: center;
  box-sizing: border-box;
`;

export const Muted = styled.p`
  margin: 0;
  max-width: ${({ max = 520 }) => max}px;
  font-family: ${fonts.family};
  font-size: ${({ small }) => (small ? 13 : 14)}px;
  line-height: 1.7;
  color: ${colors.textMuted};
  text-align: ${({ align = 'center' }) => align};
`;

export const Headline = styled.h2`
  margin: 0;
  max-width: 560px;
  font-family: ${fonts.family};
  font-size: 18px;
  line-height: 1.5;
  font-weight: 700;
  color: ${colors.heading};
  text-align: center;
  ${phone} {
    font-size: 16px;
  }
`;

export const Accent = styled.span`
  display: block;
  margin-top: 8px;
  font-size: 20px;
  font-weight: 600;
  color: ${colors.teal};
`;

export const FieldRow = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 24px;
  input {
    width: 140px;
    min-width: 0;
    text-align: left;
  }
  ${phone} {
    gap: 12px;
    input {
      width: 128px;
    }
  }
`;

// Read-only field that shows a received value (white, dark text, like Figma C3.2 / P6).
export const filledField = { background: '#fff', color: colors.text, borderColor: colors.brand[300] };

// ----- Screen / Camera status cards (C2, C4, P2, P4) ------------------------------------------

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 32px;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  ${phone} {
    gap: 14px;
  }
`;

const StatusCardRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  font-family: ${fonts.family};
  .art {
    width: 100%;
    max-width: 290px;
    aspect-ratio: 1.75;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: ${({ ok }) => (ok ? colors.success[600] : '#A3A7A6')};
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
    transition: background-color 200ms ease;
  }
  .label {
    font-size: 14px;
    color: ${colors.slate[700]};
  }
  .label b {
    font-weight: 700;
  }
  ${phone} {
    gap: 8px;
    .label {
      font-size: 12px;
    }
    .art svg {
      width: 36px;
      height: 36px;
    }
  }
`;

export const StatusCard = ({ kind, ok, action }) => {
  const Icon = kind === 'camera' ? PhotoCameraIcon : MonitorIcon;
  const name = kind === 'camera' ? 'Camera' : 'Screen';
  return (
    <StatusCardRoot ok={ok} data-testid={`${kind}-status-card`} data-calibrated={ok ? 'true' : 'false'}>
      <div className='art'>
        <Icon size={kind === 'camera' ? 46 : 50} strokeWidth={1.6} />
      </div>
      <span className='label'>
        <b>{name}:</b> {ok ? 'Calibrated Successfully' : 'Not Calibrated'}
      </span>
      {action}
    </StatusCardRoot>
  );
};

export const StatusCards = ({ screen, camera, screenAction, cameraAction }) => (
  <Cards>
    <StatusCard kind='screen' ok={screen} action={screenAction} />
    <StatusCard kind='camera' ok={camera} action={cameraAction} />
  </Cards>
);

// ----- Big method tiles ("Use Card" filled / "Use Ruler" outlined, C3.1) -----------------------

export const TileRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ count = 2 }) => count}, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 600px;
  ${phone} {
    gap: 10px;
  }
`;

export const MethodTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 124px;
  padding: 16px;
  border-radius: 10px;
  border: 2px solid ${colors.teal};
  background: ${({ filled }) => (filled ? colors.teal : '#fff')};
  color: ${({ filled }) => (filled ? '#fff' : colors.teal)};
  font-family: ${fonts.family};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, box-shadow 120ms ease;
  &:hover:not(:disabled) {
    box-shadow: 0 6px 16px rgba(37, 157, 144, 0.25);
    background: ${({ filled }) => (filled ? colors.primary : colors.brand[50])};
  }
  &:focus-visible {
    outline: 3px solid ${colors.brand[300]};
    outline-offset: 2px;
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  ${phone} {
    min-height: 96px;
    font-size: 15px;
    svg {
      width: 36px;
      height: 36px;
    }
  }
`;

// ----- Camera calibration traffic lights (C4.1) -----------------------------------------------

const pulse = keyframes`
  0% { transform: scale(0.85); opacity: 1; }
  100% { transform: scale(1.15); opacity: 0.55; }
`;

const Dot = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ bg }) => bg};
  animation: ${({ pulsing }) => (pulsing ? `${pulse} 900ms ease-in-out infinite alternate` : 'none')};
`;

const DOTS = [
  { on: colors.error[900], off: colors.error[100] },
  { on: colors.warning[400], off: '#F5E9C8' },
  { on: colors.success[500], off: '#D5E5DA' },
];

/** status: 0 not calibrated (red), 1 calibrating (amber, pulsing), 2 calibrated (green). */
export const StatusDots = ({ status = 0 }) => (
  <div style={{ display: 'flex', gap: 28, justifyContent: 'center' }} aria-hidden='true'>
    {DOTS.map((d, i) => (
      <Dot key={i} bg={i === status ? d.on : d.off} pulsing={i === 1 && status === 1} />
    ))}
  </div>
);

export const CAMERA_STATUS_LABEL = { 0: 'Not Calibrated', 1: 'Calibrating…', 2: 'Calibrated' };

export const StatusLine = styled.p`
  margin: 0;
  font-family: ${fonts.family};
  font-size: 16px;
  color: ${colors.slate[400]};
  b {
    color: ${colors.slate[900]};
    font-weight: 700;
  }
`;

// Accordion ("Use Device Measurement" / "Use Card" / "Use Ruler", C3).
export const AccordionRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  font-family: ${fonts.family};
  text-align: left;
`;

export const AccordionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  border-radius: ${radii.button}px;
  background: ${colors.slate[50]};
  color: ${colors.slate[600]};
  font-family: ${fonts.family};
  font-size: 14px;
  font-weight: ${({ isOpen }) => (isOpen ? 700 : 500)};
  text-align: left;
  cursor: pointer;
  svg {
    transition: transform 150ms ease;
    transform: rotate(${({ isOpen }) => (isOpen ? 90 : 0)}deg);
    flex-shrink: 0;
  }
  &:hover {
    background: ${colors.slate[100]};
  }
  &:focus-visible {
    outline: 2px solid ${colors.brand[300]};
  }
`;

export const AccordionBody = styled.div`
  padding: 8px 14px 16px;
  ol {
    margin: 0 0 16px;
    padding-left: 20px;
    color: ${colors.slate[600]};
    font-size: 13px;
    line-height: 1.7;
  }
  li::marker {
    font-weight: 700;
  }
  li:first-of-type {
    font-weight: ${({ boldFirst }) => (boldFirst ? 700 : 400)};
  }
`;
